import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Input,
  ListItemText,
  MenuItem,
  NativeSelect,
  Paper,
  Select,
  Tooltip,
} from '@material-ui/core';
import MaterialTable from 'material-table';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useHistory, useLocation } from 'react-router-dom';

import Loading from '../../components/Loading';
import Header from '../../layout/Header';
import Subheader from '../../layout/Subheader';
import api from '../../services/api';
import { findPermission } from '../../services/auth';
import { materialTableLocalizationPtBR } from '../../services/materialTableLocalization';
import { getDefaultPeriodoRange } from '../../utils/dateRangeValidation';

import useStyles from './style';

export const MODULO_ID_CONSULTAR_REMESSAS = 16320;
const MODULO_ID_PAGAMENTOS_REMESSAS = 18198;

const STATUS_OPTIONS = [
  { value: 0, name: 'Pendente',           label: 'Pendente',           color: '#FB8832',
    tooltip: 'Remessa gerada mas ainda nao enviada ao banco.' },
  { value: 1, name: 'Processando',        label: 'Processando',        color: '#1E88E5',
    tooltip: 'O banco ja agendou parte dos pagamentos (recebeu retorno pago ou agendado), mas ainda ha pagamentos aguardando confirmacao do banco.' },
  { value: 2, name: 'Enviado',            label: 'Enviado',            color: '#26A69A',
    tooltip: 'Arquivo transmitido para o banco; nenhum retorno chegou ainda.' },
  { value: 3, name: 'Processado',         label: 'Processado',         color: '#2E7D32',
    tooltip: 'Todos os pagamentos foram concluidos (pagos, pagos manualmente ou cancelados) sem erros.' },
  { value: 4, name: 'Erro',               label: 'Erro',               color: '#C62828',
    tooltip: 'Todos os pagamentos da remessa retornaram com erro.' },
  { value: 5, name: 'Cancelado',          label: 'Cancelado',          color: '#9E9E9E',
    tooltip: 'Todos os pagamentos da remessa foram cancelados.' },
  { value: 6, name: 'ProcessadoComErro',  label: 'Processado c/ Erro', color: '#F57C00',
    tooltip: 'Remessa concluida mas com pelo menos um pagamento em erro (pode ter havido pagamentos bem-sucedidos junto).' },
  { value: 7, name: 'EnviadoComErro',     label: 'Enviado c/ Erro',    color: '#EF6C00',
    tooltip: 'Pelo menos um pagamento ja deu erro, mas ainda ha pagamentos enviados ao banco aguardando confirmacao (remessa ainda nao concluida).' },
];

const statusToInt = (s) => {
  if (typeof s === 'number' && Number.isFinite(s)) return s;
  if (typeof s === 'string') {
    const found = STATUS_OPTIONS.find((x) => x.name === s);
    if (found) return found.value;
    const n = Number(s);
    if (Number.isFinite(n)) return n;
  }
  return -1;
};

const statusLabel = (s) => STATUS_OPTIONS.find((x) => x.value === statusToInt(s))?.label || String(s ?? '');
const statusColor = (s) => STATUS_OPTIONS.find((x) => x.value === statusToInt(s))?.color || '#777';
const statusTooltip = (s) => STATUS_OPTIONS.find((x) => x.value === statusToInt(s))?.tooltip || '';

const formatBrl = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatDate = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : '');
const formatDateTime = (d) => (d ? new Date(d).toLocaleString('pt-BR') : '');

const truncate = (s, n) => (!s ? '' : s.length > n ? s.substring(0, n) + '…' : s);

export function ConsultarRemessas() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const [isLoading, setLoading] = useState(false);
  const [destinos, setDestinos] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [rows, setRows] = useState([]);

  const [destinoPagamentoId, setDestinoPagamentoId] = useState('');
  const [bancoRemessaId, setBancoRemessaId] = useState('');
  const [dataInicial, setDataInicial] = useState(() => getDefaultPeriodoRange(1).inicial);
  const [dataFinal, setDataFinal] = useState(() => getDefaultPeriodoRange(1).final);
  const [codigoInterno, setCodigoInterno] = useState('');
  const [statusFiltro, setStatusFiltro] = useState([]);

  const [erroModal, setErroModal] = useState({ open: false, codigo: '', observacao: '' });
  const [remessaIdGerando, setRemessaIdGerando] = useState(null);
  const [remessaIdCancelando, setRemessaIdCancelando] = useState(null);
  const [cancelarModal, setCancelarModal] = useState({ open: false, row: null });

  useEffect(() => {
    if (!findPermission(MODULO_ID_CONSULTAR_REMESSAS, 'Consultar')) {
      history.replace('/app/home');
    }
  }, [history]);

  const fetchFilterOptions = async () => {
    try {
      const [d, b] = await Promise.all([
        api.get('DestinosPagamentos/Consultar?status=Ativo'),
        api.get('BancosRemessas/Consultar?status=Ativo'),
      ]);
      const destinosCarregados = d?.data?.resposta || [];
      setDestinos(destinosCarregados);
      setBancos(b?.data?.resposta || []);
      return destinosCarregados;
    } catch {
      toast.error('Erro ao carregar filtros.');
      return [];
    }
  };

  const fetchRemessasComFiltros = async (filtros) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtros.destinoPagamentoId) params.append('destinoPagamentoId', filtros.destinoPagamentoId);
      if (filtros.bancoRemessaId) params.append('bancoRemessaId', filtros.bancoRemessaId);
      if (filtros.dataInicial) params.append('dataInicial', filtros.dataInicial);
      if (filtros.dataFinal) params.append('dataFinal', filtros.dataFinal);
      if (filtros.codigoInterno) params.append('codigoInterno', filtros.codigoInterno);
      (filtros.statusFiltro || []).forEach((s) => params.append('status', s));

      const res = await api.get(`Remessas/Consultar?${params.toString()}`);
      const resposta = res?.data?.resposta;
      setRows(Array.isArray(resposta) && resposta.length && !resposta[0]?.className ? resposta : []);
    } catch {
      toast.error('Erro ao consultar remessas.');
      setRows([]);
    }
    setLoading(false);
  };

  const fetchRemessas = () =>
    fetchRemessasComFiltros({ destinoPagamentoId, bancoRemessaId, dataInicial, dataFinal, codigoInterno, statusFiltro });

  const cancelarRemessaAgora = async (row) => {
    if (!row?.remessaId) return;
    setRemessaIdCancelando(row.remessaId);
    try {
      const res = await api.post(`Remessas/${row.remessaId}/Cancelar`);
      const status = res?.data?.status;
      if (status === 'Sucesso') {
        toast.success(`Remessa ${row.codigoInterno} cancelada. Pagamentos liberados para nova remessa.`);
      } else {
        const msg = res?.data?.resposta?.[0]?.message || 'Falha ao cancelar remessa.';
        toast.error(msg);
      }
    } catch (e) {
      const msg = e?.response?.data?.resposta?.[0]?.message;
      toast.error(msg || 'Erro ao cancelar remessa.');
    } finally {
      setRemessaIdCancelando(null);
      setCancelarModal({ open: false, row: null });
      fetchRemessas();
    }
  };

  const gerarRemessaAgora = async (row) => {
    if (!row?.remessaId) return;
    setRemessaIdGerando(row.remessaId);
    try {
      const res = await api.post(`Remessas/${row.remessaId}/GerarArquivo`);
      const status = res?.data?.status;
      if (status === 'Sucesso') {
        toast.success(`Remessa ${row.codigoInterno} gerada com sucesso.`);
      } else {
        const msg = res?.data?.resposta?.[0]?.message || 'Falha ao gerar remessa.';
        toast.error(msg);
      }
    } catch (e) {
      const msg = e?.response?.data?.resposta?.[0]?.message;
      toast.error(msg || 'Erro ao gerar remessa.');
    } finally {
      setRemessaIdGerando(null);
      fetchRemessas();
    }
  };

  useEffect(() => {
    (async () => {
      const destinosCarregados = await fetchFilterOptions();
      const params = new URLSearchParams(location.search);
      const codigoParam = params.get('codigo');
      const destinoParam = params.get('destino');
      const dataIniParam = params.get('dataInicial');
      const dataFimParam = params.get('dataFinal');

      if (codigoParam) setCodigoInterno(codigoParam);
      let destinoInicial = destinoParam || '';
      if (destinoParam) {
        setDestinoPagamentoId(destinoParam);
      } else {
        const securitizadora = destinosCarregados.find(
          (d) => (d.destinoPagamento || d.nomeFantasia || '').toUpperCase() === 'SECURITIZADORA',
        );
        if (securitizadora) {
          destinoInicial = securitizadora.destinoPagamentoId;
          setDestinoPagamentoId(destinoInicial);
        }
      }
      if (dataIniParam) setDataInicial(dataIniParam);
      if (dataFimParam) setDataFinal(dataFimParam);

      if (codigoParam) {
        fetchRemessasComFiltros({
          codigoInterno: codigoParam,
          destinoPagamentoId: destinoInicial,
          bancoRemessaId: '',
          dataInicial: dataIniParam || '',
          dataFinal: dataFimParam || '',
          statusFiltro: [],
        });
      } else if (destinoParam) {
        toast.info('Filtros vindos da tela anterior pré-preenchidos. Clique em Pesquisar.');
      }
    })();
  }, []);

  const irParaPagamentosRemessas = () => {
    const params = new URLSearchParams();
    if (destinoPagamentoId) params.append('destino', destinoPagamentoId);
    if (dataInicial) params.append('dataInicial', dataInicial);
    if (dataFinal) params.append('dataFinal', dataFinal);
    if (codigoInterno) params.append('remessa', codigoInterno);
    history.push(`/app/pagamentos-remessas${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const valorTotalGeral = useMemo(
    () => rows.reduce((acc, r) => acc + Number(r.valorTotal || 0), 0),
    [rows],
  );

  const columns = useMemo(
    () => [
      { title: 'Data Pagto', field: 'dataPagamento', render: (r) => formatDate(r.dataPagamento) },
      { title: 'Dest. Pagto', field: 'destinoPagamentoDescricao' },
      { title: 'Banco Remessa', field: 'bancoRemessaNome' },
      { title: 'Geração', field: 'dataRegistro', render: (r) => formatDateTime(r.dataRegistro) },
      {
        title: 'Status',
        field: 'status',
        render: (r) => (
          <Tooltip title={statusTooltip(r.status)} arrow>
            <span className={classes.statusChip} style={{ background: statusColor(r.status) }}>
              {statusLabel(r.status)}
            </span>
          </Tooltip>
        ),
      },
      {
        title: 'Remessa ID',
        field: 'codigoInterno',
        render: (r) => (
          <span
            className={classes.linkCell}
            onClick={() => {
              const params = new URLSearchParams();
              params.append('remessa', r.codigoInterno);
              if (r.destinoPagamentoId) params.append('destino', r.destinoPagamentoId);
              history.push(`/app/pagamentos-remessas?${params.toString()}`);
            }}
          >
            {r.codigoInterno}
          </span>
        ),
      },
      { title: 'Qtde Pagtos', field: 'quantidadePagamentos' },
      {
        title: 'Valor Total',
        field: 'valorTotal',
        render: (r) => formatBrl(r.valorTotal),
        cellStyle: { textAlign: 'right' },
      },
      {
        title: 'Arquivo Envio',
        field: 'nomeArquivoEnvio',
        render: (r) => (
          <Tooltip title={r.nomeArquivoEnvio || ''}>
            <span>{truncate(r.nomeArquivoEnvio, 22)}</span>
          </Tooltip>
        ),
      },
      {
        title: 'Erro',
        field: 'erro',
        sorting: false,
        render: (r) =>
          statusToInt(r.status) === 4 || statusToInt(r.status) === 6 || statusToInt(r.status) === 7 ? (
            <span
              className={classes.errorLink}
              onClick={() =>
                setErroModal({
                  open: true,
                  codigo: r.codigoInterno,
                  observacao: r.observacao || 'Sem detalhe.',
                })
              }
            >
              Ver
            </span>
          ) : null,
      },
      {
        title: 'Ações',
        field: 'acoes',
        sorting: false,
        render: (r) => {
          const st = statusToInt(r.status);
          const isAcionavel = st === 0 || st === 4;
          if (!isAcionavel) return null;

          // Remessa em Erro só pode ser cancelada — não dá pra tentar gerar de novo direto por cima do erro.
          const podeGerar = st === 0 && !!findPermission(MODULO_ID_CONSULTAR_REMESSAS, 'Alterar');
          const podeCancelar = !!findPermission(MODULO_ID_CONSULTAR_REMESSAS, 'Remover');
          const isGerando = remessaIdGerando === r.remessaId;
          const isCancelando = remessaIdCancelando === r.remessaId;

          return (
            <span style={{ display: 'inline-flex', gap: 6 }}>
              {podeGerar && (
                <Tooltip title="Gerar arquivo agora (sem aguardar o agendamento)">
                  <span>
                    <button
                      type="button"
                      className={classes.buttonFilter}
                      style={{ padding: '4px 10px', marginTop: 0, opacity: isGerando ? 0.6 : 1 }}
                      disabled={isGerando || isCancelando}
                      onClick={() => gerarRemessaAgora(r)}
                    >
                      {isGerando ? 'Gerando...' : 'Gerar'}
                    </button>
                  </span>
                </Tooltip>
              )}
              {podeCancelar && (
                <Tooltip title="Cancelar remessa e liberar pagamentos vinculados">
                  <span>
                    <button
                      type="button"
                      className={classes.buttonFilter}
                      style={{
                        padding: '4px 10px',
                        marginTop: 0,
                        background: '#C62828',
                        opacity: isCancelando ? 0.6 : 1,
                      }}
                      disabled={isGerando || isCancelando}
                      onClick={() => setCancelarModal({ open: true, row: r })}
                    >
                      {isCancelando ? 'Cancelando...' : 'Cancelar'}
                    </button>
                  </span>
                </Tooltip>
              )}
            </span>
          );
        },
      },
    ],
    [rows, remessaIdGerando, remessaIdCancelando],
  );

  return (
    <div className={classes.root}>
      <Header />
      <Subheader title="Consultar Remessas" back="/app/home" />
      <div className={classes.content}>
        <div className={classes.rootForm}>
          <header>
          <Grid container className={classes.searchContainer} spacing={2}>
            <Grid item xs={3} className={classes.item}>
              <label className={classes.label}>Destino de Pagamento</label>
              <Paper className={classes.medium} elevation={0}>
                <NativeSelect
                  className={classes.input}
                  value={destinoPagamentoId}
                  onChange={(e) => setDestinoPagamentoId(e.target.value)}
                >
                  <option value="">Todos</option>
                  {destinos.map((d) => (
                    <option key={d.destinoPagamentoId} value={d.destinoPagamentoId}>
                      {d.destinoPagamento || d.nomeFantasia}
                    </option>
                  ))}
                </NativeSelect>
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <label className={classes.label}>Banco Remessa</label>
              <Paper className={classes.medium} elevation={0}>
                <NativeSelect
                  className={classes.input}
                  value={bancoRemessaId}
                  onChange={(e) => setBancoRemessaId(e.target.value)}
                >
                  <option value="">Todos</option>
                  {bancos.map((b) => (
                    <option key={b.bancoRemessaId} value={b.bancoRemessaId}>
                      {b.nome}
                    </option>
                  ))}
                </NativeSelect>
              </Paper>
            </Grid>
            <Grid item xs={2}>
              <label className={classes.label}>Data Inicial</label>
              <Paper className={classes.medium} elevation={0}>
                <Input
                  type="date"
                  className={classes.input}
                  value={dataInicial}
                  onChange={(e) => setDataInicial(e.target.value)}
                />
              </Paper>
            </Grid>
            <Grid item xs={2}>
              <label className={classes.label}>Data Final</label>
              <Paper className={classes.medium} elevation={0}>
                <Input
                  type="date"
                  className={classes.input}
                  value={dataFinal}
                  onChange={(e) => setDataFinal(e.target.value)}
                />
              </Paper>
            </Grid>
            <Grid item xs={2}>
              <label className={classes.label}>Remessa</label>
              <Paper className={classes.medium} elevation={0}>
                <Input
                  className={classes.input}
                  value={codigoInterno}
                  onChange={(e) => setCodigoInterno(e.target.value)}
                />
              </Paper>
            </Grid>
            <Grid item xs={2} className={classes.item}>
              <label className={classes.label}>Status</label>
              <Paper className={classes.medium} elevation={0}>
                <Select
                  multiple
                  disableUnderline
                  displayEmpty
                  className={classes.input}
                  value={statusFiltro}
                  onChange={(e) => setStatusFiltro(e.target.value)}
                  renderValue={(selected) => {
                    if (!selected || selected.length === 0) return 'Todos';
                    if (selected.length === STATUS_OPTIONS.length) return 'Todos';
                    if (selected.length === 1) return statusLabel(selected[0]);
                    return `${selected.length} selecionados`;
                  }}
                  MenuProps={{
                    variant: 'menu',
                    getContentAnchorEl: null,
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                    autoFocus: false,
                    PaperProps: { style: { maxHeight: 220, overflowY: 'scroll', width: 240 } },
                    MenuListProps: { style: { padding: 0, width: 240 } },
                  }}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s.value} value={s.value} dense style={{ paddingLeft: 4 }}>
                      <Checkbox
                        checked={statusFiltro.indexOf(s.value) > -1}
                        size="small"
                        style={{ color: s.color, padding: 4 }}
                      />
                      <span
                        style={{
                          display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                          background: s.color, marginRight: 8, flexShrink: 0,
                        }}
                      />
                      <ListItemText
                        primary={s.label}
                        primaryTypographyProps={{ style: { fontSize: 13 } }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </Paper>
            </Grid>
          </Grid>
          </header>
          <div className={classes.actions}>
            {rows.length > 0 ? (
              <span className={classes.totalGeral}>{`Valor Total Remessas: ${formatBrl(valorTotalGeral)}`}</span>
            ) : (
              <span />
            )}
            <div>
              <button className={classes.buttonFilter} onClick={fetchRemessas}>
                Pesquisar
              </button>
              {!!findPermission(MODULO_ID_PAGAMENTOS_REMESSAS, 'Consultar') && (
                <button
                  className={classes.buttonAction}
                  onClick={irParaPagamentosRemessas}
                  title="Abrir Pagamentos Remessas mantendo o contexto"
                >
                  Ver em Pagamentos Remessas
                </button>
              )}
            </div>
          </div>
          {isLoading ? (
            <Loading />
          ) : (
            <MaterialTable
              options={{
                showTitle: false,
                search: false,
                toolbar: false,
                paging: false,
                filtering: false,
                headerStyle: { backgroundColor: '#F5F5F5', fontSize: '13px' },
                rowStyle: { fontSize: '13px' },
              }}
              localization={materialTableLocalizationPtBR}
              columns={columns}
              data={rows}
            />
          )}
        </div>
      </div>

      <Dialog open={erroModal.open} onClose={() => setErroModal({ open: false, codigo: '', observacao: '' })}>
        <DialogTitle style={{ background: '#C62828', color: '#fff' }}>
          Detalhe do Erro — Remessa {erroModal.codigo}
        </DialogTitle>
        <DialogContent style={{ minWidth: '420px', padding: '16px', whiteSpace: 'pre-line' }}>{erroModal.observacao}</DialogContent>
        <DialogActions>
          <button className={classes.buttonFilter} onClick={() => setErroModal({ open: false, codigo: '', observacao: '' })}>
            Fechar
          </button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelarModal.open} onClose={() => setCancelarModal({ open: false, row: null })}>
        <DialogTitle style={{ background: '#C62828', color: '#fff' }}>
          Cancelar Remessa {cancelarModal.row?.codigoInterno || ''}
        </DialogTitle>
        <DialogContent style={{ minWidth: '460px', padding: '16px' }}>
          <p style={{ marginTop: 0 }}>
            Tem certeza que deseja cancelar esta remessa? Os pagamentos vinculados
            voltarão para <strong>Pendente</strong> e poderão ser usados em outra remessa.
          </p>
          <p style={{ marginBottom: 0, color: '#777', fontSize: 13 }}>
            Pagamentos já confirmados (Pago / Pago Manual) ou Cancelados não serão alterados.
          </p>
        </DialogContent>
        <DialogActions>
          <button
            className={classes.buttonFilter}
            style={{ background: '#9E9E9E' }}
            onClick={() => setCancelarModal({ open: false, row: null })}
          >
            Voltar
          </button>
          <button
            className={classes.buttonFilter}
            style={{ background: '#C62828' }}
            disabled={remessaIdCancelando !== null}
            onClick={() => cancelarRemessaAgora(cancelarModal.row)}
          >
            {remessaIdCancelando !== null ? 'Cancelando...' : 'Confirmar cancelamento'}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ConsultarRemessas;
